import React, { useCallback, useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import PlaqueEditorModal from './PlaqueEditorModal';
import { getSubscriptionTypeFromToken } from '../../../utils/jwt';

const LANGUAGE_OPTIONS = [
  { code: 'vi', label: 'Vietnamese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ru', label: 'Russian' },
  { code: 'zh', label: 'Chinese (Simplified)' },
  { code: 'zh-TW', label: 'Chinese (Traditional)' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ja', label: 'Japanese' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'es', label: 'Spanish' },
  { code: 'th', label: 'Thai' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'en', label: 'English' },
];

const DEFAULT_TRANSLATION_PROMPT = [
  'Translate the following informational plaque content for a tourist or historical site.',
  'Use a natural, friendly, professional tone.',
  'Preserve all formatting, line breaks, and any image markers like [[image:123]] exactly as-is.',
  'Do not add extra commentary.',
  'Return JSON only with keys: title, translatedText.',
].join('\n');

const DEFAULT_SITE_PLAQUE_THEME = Object.freeze({
  backgroundColor: '#1e293b',
  surfaceColor: '#0f172a',
  textColor: '#e2e8f0',
});

const normalizeHexColor = (value, fallback) => {
  const normalized = String(value || '').trim().toLowerCase();
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized : fallback;
};

const parseJsonObject = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (err) {
    return null;
  }
};

const normalizeSitePlaqueThemeForStorage = (value) => {
  const raw = parseJsonObject(value);
  if (!raw) return null;
  const hasAnyValue = ['backgroundColor', 'background_color', 'surfaceColor', 'surface_color', 'textColor', 'text_color']
    .some((key) => Object.prototype.hasOwnProperty.call(raw, key));
  if (!hasAnyValue) return null;
  return {
    backgroundColor: normalizeHexColor(raw.backgroundColor ?? raw.background_color, DEFAULT_SITE_PLAQUE_THEME.backgroundColor),
    surfaceColor: normalizeHexColor(raw.surfaceColor ?? raw.surface_color, DEFAULT_SITE_PLAQUE_THEME.surfaceColor),
    textColor: normalizeHexColor(raw.textColor ?? raw.text_color, DEFAULT_SITE_PLAQUE_THEME.textColor),
  };
};

const resolveSitePlaqueThemeForEditor = (value) => {
  const normalized = normalizeSitePlaqueThemeForStorage(value);
  return normalized || { ...DEFAULT_SITE_PLAQUE_THEME };
};

function PlaqueColorField({ label, value, onChange, helpText = '' }) {
  return (
    <label className="block text-sm text-gray-200">
      <span className="font-medium text-white">{label}</span>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-14 rounded border border-gray-700 bg-gray-950 p-1"
        />
        <input
          type="text"
          value={value}
          readOnly
          className="w-full rounded bg-gray-950 px-3 py-2 text-white border border-gray-800 font-mono"
          spellCheck={false}
        />
      </div>
      {helpText ? (
        <div className="mt-2 text-xs text-gray-500">{helpText}</div>
      ) : null}
    </label>
  );
}

const formatTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const buildLandingUrl = (landingKey) => {
  if (!landingKey) return '';
  if (typeof window === 'undefined') return `/lp/p/${landingKey}`;
  return `${window.location.origin}/lp/p/${landingKey}`;
};

const normalizeAreaFilterText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const normalizeAreaFilterSlug = (value) =>
  normalizeAreaFilterText(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const MONETISATION_MODE_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'standard', label: 'Standard' },
  { value: 'full', label: 'Full' },
];

const normalizeMonetisationMode = (value, fallback = 'off') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (MONETISATION_MODE_OPTIONS.some((option) => option.value === normalized)) return normalized;
  return fallback;
};

const getMonetisationModeMeta = (value, { enforced = false } = {}) => {
  const normalized = normalizeMonetisationMode(value, 'off');
  const option = MONETISATION_MODE_OPTIONS.find((entry) => entry.value === normalized);
  const classesByMode = {
    off: 'bg-gray-800 text-gray-200',
    subtle: 'bg-sky-900/70 text-sky-100',
    standard: 'bg-amber-900/70 text-amber-100',
    full: 'bg-emerald-900/70 text-emerald-100',
  };
  return {
    label: `${option?.label || 'Off'}${enforced ? ' (Enforced)' : ''}`,
    classes: classesByMode[normalized] || classesByMode.off,
  };
};

export default function InfoPlaques({ site, area, assets, onRefreshAssets, onEditWidget, openPlaqueId = null, openRequestKey = 0 }) {
  const [view, setView] = useState('plaques');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plaques, setPlaques] = useState([]);
  const [maps, setMaps] = useState([]);
  const [mapsLoading, setMapsLoading] = useState(false);
  const [legendMapId, setLegendMapId] = useState(null);
  const [legendLabelEnabled, setLegendLabelEnabled] = useState(true);
  const [legendSaving, setLegendSaving] = useState(false);
  const [sitePlaqueThemeSettings, setSitePlaqueThemeSettings] = useState(() => ({ ...DEFAULT_SITE_PLAQUE_THEME }));
  const [loadedSitePlaqueThemeSettings, setLoadedSitePlaqueThemeSettings] = useState(() => ({ ...DEFAULT_SITE_PLAQUE_THEME }));
  const [plaqueThemeSaving, setPlaqueThemeSaving] = useState(false);
  const [plaqueThemeMessage, setPlaqueThemeMessage] = useState('');
  const [plaqueThemeError, setPlaqueThemeError] = useState('');
  const [plaquesPanelOpen, setPlaquesPanelOpen] = useState({
    baseColors: false,
    siteMaps: false,
  });
  const [editor, setEditor] = useState({ open: false, plaqueId: null });
  const [qrCodes, setQrCodes] = useState({});
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [translationsPrompt, setTranslationsPrompt] = useState(DEFAULT_TRANSLATION_PROMPT);
  const [translationOptions, setTranslationOptions] = useState({
    maintainTitles: true,
  });
  const [translationsState, setTranslationsState] = useState({
    running: false,
    error: '',
    completed: 0,
    total: 0,
    results: [],
  });
  const [bulkRemoveRunning, setBulkRemoveRunning] = useState(false);
  const [bulkRemoveMessage, setBulkRemoveMessage] = useState('');
  const siteId = site?.id || null;
  const subscriptionType = String(
    site?.subscriptionType
    || site?.subscription_type
    || getSubscriptionTypeFromToken(localStorage.getItem('token'))
    || 'free'
  ).trim().toLowerCase();
  const monetisationIsEnforced = subscriptionType === 'free';
  const isActivityHubSurface = normalizeAreaFilterText(area?.area_type || area?.areaType || '') === 'activity_hub';
  const areaFilterId = useMemo(() => {
    if (!siteId || !area?.id) return null;
    const areaType = normalizeAreaFilterText(area?.area_type || area?.areaType || '');
    if (areaType && areaType !== 'activity_hub') return null;
    const areaSlug = normalizeAreaFilterSlug(area?.slug || area?.name || '');
    const siteSlug = normalizeAreaFilterSlug(site?.destination_slug || site?.destinationSlug || site?.name || '');
    const areaName = normalizeAreaFilterText(area?.name || '');
    const siteName = normalizeAreaFilterText(site?.name || '');
    if ((areaSlug && siteSlug && areaSlug === siteSlug) || (areaName && siteName && areaName === siteName)) {
      return null;
    }
    return Number(area.id) || null;
  }, [area, site, siteId]);

  useEffect(() => {
    const nextTheme = resolveSitePlaqueThemeForEditor(site?.plaqueTheme || site?.plaque_theme_json || null);
    setSitePlaqueThemeSettings(nextTheme);
    setLoadedSitePlaqueThemeSettings(nextTheme);
    setPlaqueThemeMessage('');
    setPlaqueThemeError('');
  }, [site?.plaqueTheme, site?.plaque_theme_json, siteId]);

  const fetchPlaques = useCallback(async () => {
    if (!siteId) {
      setPlaques([]);
      return;
    }
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('siteId', String(siteId));
      if (areaFilterId) params.set('areaId', String(areaFilterId));
      const query = `/api/plaques?${params.toString()}`;
      const res = await fetch(query, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load plaques (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      setPlaques(Array.isArray(data.plaques) ? data.plaques : Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch plaques', err);
      setError(err.message || 'Failed to load plaques.');
    } finally {
      setLoading(false);
    }
  }, [areaFilterId, siteId]);

  useEffect(() => {
    fetchPlaques();
  }, [fetchPlaques]);

  useEffect(() => {
    if (!openPlaqueId) return;
    setView('plaques');
    setEditor({ open: true, plaqueId: openPlaqueId });
  }, [openPlaqueId, openRequestKey]);


  const fetchMaps = useCallback(async () => {
    if (!siteId) {
      setMaps([]);
      return;
    }
    const token = localStorage.getItem('token');
    setMapsLoading(true);
    try {
      const res = await fetch(`/api/site-maps?siteId=${encodeURIComponent(siteId)}`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load site maps (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      setMaps(Array.isArray(data.maps) ? data.maps : []);
    } catch (err) {
      console.error('Failed to fetch site maps', err);
      setMaps([]);
    } finally {
      setMapsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchMaps();
  }, [fetchMaps]);

  useEffect(() => {
    if (!maps.length) {
      setLegendMapId(null);
      setLegendLabelEnabled(true);
      return;
    }
    const exists = legendMapId && maps.some((map) => String(map.id) === String(legendMapId));
    const nextId = exists ? legendMapId : maps[0].id;
    setLegendMapId(nextId);
    const selected = maps.find((map) => String(map.id) === String(nextId));
    const enabled = selected?.showLegendLabel !== false;
    setLegendLabelEnabled(enabled);
  }, [legendMapId, maps]);

  const openSiteMapsSettings = useCallback(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    params.set('settingsSubTab', 'Site Maps');
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  }, []);

  const togglePlaquesPanel = useCallback((panelKey) => {
    setPlaquesPanelOpen((prev) => ({
      ...prev,
      [panelKey]: !prev[panelKey],
    }));
  }, []);

  const persistSitePlaqueTheme = useCallback(async () => {
    if (!siteId) return;
    const hasThemeChanges = JSON.stringify(sitePlaqueThemeSettings) !== JSON.stringify(loadedSitePlaqueThemeSettings);
    if (!hasThemeChanges) {
      setPlaqueThemeMessage('No plaque colour changes to save.');
      setPlaqueThemeError('');
      return;
    }

    setPlaqueThemeSaving(true);
    setPlaqueThemeMessage('');
    setPlaqueThemeError('');
    try {
      const nextTheme = normalizeSitePlaqueThemeForStorage(sitePlaqueThemeSettings);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/sites/${siteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plaqueTheme: nextTheme }),
      });
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(body || `Failed to save plaque colours (${response.status})`);
      }

      const resolvedTheme = resolveSitePlaqueThemeForEditor(nextTheme);
      setLoadedSitePlaqueThemeSettings(resolvedTheme);
      setSitePlaqueThemeSettings(resolvedTheme);
      setPlaqueThemeMessage('Plaque base colours saved for this site.');
    } catch (err) {
      console.error('Failed to save site plaque theme', err);
      setPlaqueThemeError(err.message || 'Failed to save plaque colours.');
    } finally {
      setPlaqueThemeSaving(false);
    }
  }, [loadedSitePlaqueThemeSettings, siteId, sitePlaqueThemeSettings]);

  const rows = useMemo(() => {
    return (Array.isArray(plaques) ? plaques : []).map((plaque) => {
      const languages = Array.isArray(plaque.languages)
        ? plaque.languages
        : Array.isArray(plaque.translations)
          ? plaque.translations.map((t) => t.language_code || t.languageCode).filter(Boolean)
          : [];
      return {
        ...plaque,
        languages,
      };
    });
  }, [plaques]);

  const toggleLanguage = useCallback((code) => {
    setSelectedLanguages((prev) => {
      if (prev.includes(code)) return prev.filter((item) => item !== code);
      return [...prev, code];
    });
  }, []);


  const upsertTranslationResult = useCallback((nextResult) => {
    if (!nextResult?.plaqueId) return;
    setTranslationsState((prev) => {
      const current = Array.isArray(prev.results) ? prev.results : [];
      const filtered = current.filter((row) => row.plaqueId !== nextResult.plaqueId);
      return { ...prev, results: [...filtered, nextResult] };
    });
  }, []);

  const handleTranslateAll = useCallback(async () => {
    if (!siteId) return;
    if (!selectedLanguages.length) {
      alert('Select at least one language.');
      return;
    }
    if (!rows.length) {
      alert('No plaques to translate.');
      return;
    }

    const token = localStorage.getItem('token');
    const targets = rows.filter((plaque) => Boolean(plaque?.id));
    setTranslationsState({
      running: true,
      error: '',
      completed: 0,
      total: targets.length,
      results: [],
    });

    try {
      for (const plaque of targets) {
        upsertTranslationResult({
          plaqueId: plaque.id,
          title: plaque.title || `Plaque #${plaque.id}`,
          status: 'running',
          message: 'Translating…',
        });

        try {
          const res = await fetch(`/api/plaques/${plaque.id}/translate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              languages: selectedLanguages,
              prompt: translationsPrompt,
              maintainTitles: translationOptions.maintainTitles,
            }),
          });
          if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(body || `Translate failed (${res.status})`);
          }
          upsertTranslationResult({
            plaqueId: plaque.id,
            title: plaque.title || `Plaque #${plaque.id}`,
            status: 'ok',
            message: 'Updated',
          });
        } catch (err) {
          upsertTranslationResult({
            plaqueId: plaque.id,
            title: plaque.title || `Plaque #${plaque.id}`,
            status: 'error',
            message: err.message || 'Translate failed.',
          });
        } finally {
          setTranslationsState((prev) => ({
            ...prev,
            completed: prev.completed + 1,
          }));
        }
      }
    } catch (err) {
      console.error('Bulk translate failed', err);
      setTranslationsState((prev) => ({
        ...prev,
        error: err.message || 'Bulk translate failed.',
      }));
    } finally {
      setTranslationsState((prev) => ({
        ...prev,
        running: false,
      }));
      fetchPlaques();
    }
  }, [fetchPlaques, rows, selectedLanguages, siteId, translationOptions.maintainTitles, translationsPrompt, upsertTranslationResult]);

  const handleRemoveTranslationsAcrossPlaques = useCallback(async () => {
    if (!siteId) return;
    const languagesToRemove = selectedLanguages.filter((code) => String(code || '').toLowerCase() !== 'en');
    if (!languagesToRemove.length) {
      alert('Select at least one non-English language to remove.');
      return;
    }
    const ok = window.confirm(
      `Remove ${languagesToRemove.join(', ')} from all plaques for ${site?.name || 'this site'}?`
    );
    if (!ok) return;

    const token = localStorage.getItem('token');
    setBulkRemoveRunning(true);
    setBulkRemoveMessage('');
    setTranslationsState((prev) => ({ ...prev, error: '' }));
    try {
      const res = await fetch('/api/plaques/translations/bulk', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          siteId,
          languages: languagesToRemove,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `Remove failed (${res.status})`);
      }
      setBulkRemoveMessage(
        `Removed ${Number(data?.removedTranslations || 0)} translation${Number(data?.removedTranslations || 0) === 1 ? '' : 's'} across ${Number(data?.affectedPlaques || 0)} plaque${Number(data?.affectedPlaques || 0) === 1 ? '' : 's'}.`
      );
      await fetchPlaques();
    } catch (err) {
      console.error('Bulk remove translations failed', err);
      setTranslationsState((prev) => ({
        ...prev,
        error: err.message || 'Bulk remove translations failed.',
      }));
    } finally {
      setBulkRemoveRunning(false);
    }
  }, [fetchPlaques, selectedLanguages, site?.name, siteId]);

  useEffect(() => {
    let cancelled = false;

    async function generate() {
      const next = {};
      for (const plaque of rows) {
        const landingKey = plaque.landing_key || plaque.landingKey;
        if (!landingKey) continue;
        const url = buildLandingUrl(landingKey);
        try {
          const dataUrl = await QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 1, width: 160 });
          next[landingKey] = dataUrl;
        } catch (err) {
          console.warn('Failed to generate QR code', err);
        }
      }
      if (!cancelled) {
        setQrCodes(next);
      }
    }

    generate();

    return () => {
      cancelled = true;
    };
  }, [rows]);

  const handleDelete = useCallback(async (plaque) => {
    if (!plaque?.id) return;
    const ok = window.confirm(`Delete plaque "${plaque.title}"? This cannot be undone.`);
    if (!ok) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/plaques/${plaque.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to delete plaque (${res.status})`);
      }
      await fetchPlaques();
    } catch (err) {
      console.error('Failed to delete plaque', err);
      alert(err.message || 'Failed to delete plaque.');
    }
  }, [fetchPlaques]);

  const handleCopyLandingUrl = useCallback(async (landingKey) => {
    const url = buildLandingUrl(landingKey);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      alert('Landing URL copied to clipboard.');
    } catch (err) {
      console.warn('Clipboard copy failed', err);
      window.prompt('Copy landing URL:', url);
    }
  }, []);

  const handleUpdatePlaqueMap = useCallback(async (plaqueId, mapId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/plaques/${plaqueId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mapId: mapId ? Number(mapId) : null,
          mapMarkerEnabled: false,
          mapIncludeInLegend: false,
          mapMarkerX: null,
          mapMarkerY: null,
          mapLegendOrder: null,
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to update plaque (${res.status})`);
      }
      await fetchPlaques();
    } catch (err) {
      console.error('Failed to update plaque map', err);
      alert(err.message || 'Failed to update plaque map.');
    }
  }, [fetchPlaques]);

  const handleApplyMapToAll = useCallback(async (mapId) => {
    if (!rows.length) return;
    const ok = window.confirm('Apply this map to all plaques? This clears existing “You Are Here” markers.');
    if (!ok) return;
    for (const plaque of rows) {
      await handleUpdatePlaqueMap(plaque.id, mapId || null);
    }
  }, [rows, handleUpdatePlaqueMap]);

  const handleSaveLegendLabel = useCallback(async (nextValue, mapIdOverride = null) => {
    const mapId = mapIdOverride || legendMapId;
    if (!mapId) return;
    const token = localStorage.getItem('token');
    setLegendSaving(true);
    try {
      const res = await fetch(`/api/site-maps/${mapId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ showLegendLabel: Boolean(nextValue) }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to update legend label (${res.status})`);
      }
      setLegendLabelEnabled(Boolean(nextValue));
      await fetchMaps();
    } catch (err) {
      console.error('Failed to update legend label setting', err);
      setError(err.message || 'Failed to update legend label setting.');
    } finally {
      setLegendSaving(false);
    }
  }, [fetchMaps, legendMapId]);

  return (
    <div className="bg-gray-900 rounded p-4">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-lg font-semibold">Info Plaques</div>
          <div className="text-sm text-gray-400">Create permanent QR codes for informational landing pages.</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 rounded bg-gray-700 text-white"
            onClick={fetchPlaques}
            disabled={!siteId || loading}
          >
            Refresh
          </button>
          <button
            className="px-3 py-1 rounded bg-indigo-600 text-white"
            onClick={() => setEditor({ open: true, plaqueId: null })}
            disabled={!siteId}
          >
            New Plaque
          </button>
        </div>
      </div>

      {!siteId && (
        <div className="text-gray-400">Select a site to manage plaques.</div>
      )}

      {error && (
        <div className="mb-3 text-red-300 text-sm">{error}</div>
      )}

      {siteId && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              className={`px-3 py-1 rounded text-white ${view === 'plaques' ? 'bg-indigo-600' : 'bg-gray-700'}`}
              onClick={() => setView('plaques')}
            >
              Plaques
            </button>
            <button
              className={`px-3 py-1 rounded text-white ${view === 'translations' ? 'bg-indigo-600' : 'bg-gray-700'}`}
              onClick={() => setView('translations')}
            >
              Manage Translations
            </button>
          </div>

          {view === 'plaques' && (
            <div className="overflow-x-auto">
              <div className="mb-3 text-sm text-gray-400">
                {areaFilterId
                  ? `Filtering plaques for ${area?.name || 'this activity'}.`
                  : `Showing all plaques for ${site?.name || 'this destination'}.`}
              </div>
              <div className="mb-4 space-y-3">
                <div className="rounded border border-gray-800 bg-gray-950">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                    onClick={() => togglePlaquesPanel('baseColors')}
                  >
                    <div>
                      <div className="font-semibold text-gray-100">Plaque Base Colours</div>
                      <div className="text-xs text-gray-400">
                        Per-site colours for all Info Plaques on this site.
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {plaquesPanelOpen.baseColors ? 'Hide' : 'Show'}
                    </div>
                  </button>
                  {plaquesPanelOpen.baseColors && (
                    <div className="border-t border-gray-800 p-4">
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                        <PlaqueColorField
                          label="Page Background"
                          value={sitePlaqueThemeSettings.backgroundColor}
                          onChange={(value) => setSitePlaqueThemeSettings((prev) => ({
                            ...prev,
                            backgroundColor: value,
                          }))}
                          helpText="Outer page background behind the plaque card."
                        />
                        <PlaqueColorField
                          label="Plaque Background"
                          value={sitePlaqueThemeSettings.surfaceColor}
                          onChange={(value) => setSitePlaqueThemeSettings((prev) => ({
                            ...prev,
                            surfaceColor: value,
                          }))}
                          helpText="Main plaque card and sticky navigation surface."
                        />
                        <PlaqueColorField
                          label="Text Colour"
                          value={sitePlaqueThemeSettings.textColor}
                          onChange={(value) => setSitePlaqueThemeSettings((prev) => ({
                            ...prev,
                            textColor: value,
                          }))}
                          helpText="Primary text colour for plaque titles and body copy."
                        />
                      </div>
                      {(plaqueThemeMessage || plaqueThemeError) && (
                        <div className={`mt-3 text-sm ${plaqueThemeError ? 'text-red-300' : 'text-emerald-300'}`}>
                          {plaqueThemeError || plaqueThemeMessage}
                        </div>
                      )}
                      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="text-xs text-gray-400">
                          These settings apply to all plaques for {site?.name || 'this site'}.
                        </div>
                        <button
                          className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                          type="button"
                          onClick={persistSitePlaqueTheme}
                          disabled={plaqueThemeSaving}
                        >
                          {plaqueThemeSaving ? 'Saving…' : 'Save Plaque Colours'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded border border-gray-800 bg-gray-950">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                    onClick={() => togglePlaquesPanel('siteMaps')}
                  >
                    <div>
                      <div className="font-semibold text-gray-100">Site Maps</div>
                      <div className="text-xs text-gray-400">
                        Shared maps and legend settings for plaque pages.
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {plaquesPanelOpen.siteMaps ? 'Hide' : 'Show'}
                    </div>
                  </button>
                  {plaquesPanelOpen.siteMaps && (
                    <div className="border-t border-gray-800 p-4 text-sm text-gray-300">
                      {mapsLoading && <div className="text-gray-400">Loading maps…</div>}
                      {!mapsLoading && maps.length === 0 && (
                        <div className="flex flex-wrap items-center gap-2 text-gray-400">
                          <span>No maps uploaded yet.</span>
                          <button
                            className="text-indigo-300 underline"
                            type="button"
                            onClick={openSiteMapsSettings}
                          >
                            Open Site Maps to upload a map
                          </button>
                        </div>
                      )}
                      {!mapsLoading && maps.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3">
                          <span>{maps.length} map{maps.length === 1 ? '' : 's'} available.</span>
                          <button
                            className="text-indigo-300 underline"
                            type="button"
                            onClick={openSiteMapsSettings}
                          >
                            Edit legend in Site Maps
                          </button>
                          <label className="flex items-center gap-2">
                            <span>Apply map to all plaques</span>
                            <select
                              className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm"
                              defaultValue=""
                              onChange={(e) => handleApplyMapToAll(e.target.value || null)}
                            >
                              <option value="">Select a map…</option>
                              {maps.map((map) => (
                                <option key={map.id} value={map.id}>
                                  {map.name || `Map #${map.id}`}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="flex items-center gap-2">
                            <span>Show “You Are Here” in Legend</span>
                            {maps.length > 1 && (
                              <select
                                className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm"
                                value={legendMapId || ''}
                                onChange={(e) => {
                                  const nextId = e.target.value || null;
                                  setLegendMapId(nextId);
                                  const selected = maps.find((map) => String(map.id) === String(nextId));
                                  setLegendLabelEnabled(selected?.showLegendLabel !== false);
                                }}
                              >
                                {maps.map((map) => (
                                  <option key={`legend-map-${map.id}`} value={map.id}>
                                    {map.name || `Map #${map.id}`}
                                  </option>
                                ))}
                              </select>
                            )}
                            <input
                              type="checkbox"
                              className="accent-indigo-500"
                              checked={legendLabelEnabled}
                              disabled={!legendMapId || legendSaving}
                              onChange={(e) => handleSaveLegendLabel(e.target.checked)}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-300">
                    <th className="py-2 pr-3">Title</th>
                    {!isActivityHubSurface && (
                      <th className="py-2 pr-3">Ads</th>
                    )}
                    <th className="py-2 pr-3">QR</th>
                    <th className="py-2 pr-3">Landing Key</th>
                    <th className="py-2 pr-3">Translations</th>
                    <th className="py-2 pr-3">Map</th>
                    <th className="py-2 pr-3">Updated</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={isActivityHubSurface ? 7 : 8} className="py-3 text-gray-400">Loading…</td>
                    </tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={isActivityHubSurface ? 7 : 8} className="py-3 text-gray-400">No plaques yet.</td>
                    </tr>
                  )}
                  {!loading && rows.map((plaque) => (
                    <tr key={plaque.id} className="border-t border-gray-800">
                      <td className="py-2 pr-3">
                        <div className="font-semibold">{plaque.title}</div>
                        {plaque.landing_key && (
                          <button
                            className="text-xs text-indigo-300 underline mt-1"
                            onClick={() => handleCopyLandingUrl(plaque.landing_key)}
                          >
                            Copy landing URL
                          </button>
                        )}
                      </td>
                      {!isActivityHubSurface && (
                        <td className="py-2 pr-3">
                          {(() => {
                            const effectiveMode = monetisationIsEnforced
                              ? 'full'
                              : normalizeMonetisationMode(
                                  plaque.monetisationMode || plaque.monetisation_mode || site?.defaultMonetisationMode || 'off',
                                  'off'
                                );
                            const badge = getMonetisationModeMeta(effectiveMode, { enforced: monetisationIsEnforced });
                            return (
                              <span className={`inline-flex rounded-full px-2 py-1 text-[11px] font-semibold ${badge.classes}`}>
                                {badge.label}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      <td className="py-2 pr-3">
                        {(() => {
                          const landingKey = plaque.landing_key || plaque.landingKey;
                          const qr = landingKey ? qrCodes[landingKey] : null;
                          const url = landingKey ? buildLandingUrl(landingKey) : '';
                          if (!landingKey) return <span className="text-gray-500">—</span>;
                          return (
                            <a href={url} target="_blank" rel="noreferrer" title="Open landing page">
                              {qr ? (
                                <img
                                  src={qr}
                                  alt="QR code"
                                  className="bg-white p-1 rounded"
                                  style={{ width: 64, height: 64 }}
                                />
                              ) : (
                                <div className="text-xs text-gray-500">Generating…</div>
                              )}
                            </a>
                          );
                        })()}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs text-gray-200">
                        {plaque.landing_key || plaque.landingKey || ''}
                      </td>
                      <td className="py-2 pr-3 text-gray-200">
                        {(plaque.languages || []).length ? plaque.languages.join(', ') : <span className="text-gray-500">—</span>}
                      </td>
                      <td className="py-2 pr-3 text-gray-200">
                        {maps.length === 0 ? (
                          <span className="text-gray-500">—</span>
                        ) : (
                          <select
                            className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs"
                            value={plaque.map_id || plaque.mapId || ''}
                            onChange={(e) => handleUpdatePlaqueMap(plaque.id, e.target.value || null)}
                          >
                            <option value="">No map</option>
                            {maps.map((map) => (
                              <option key={map.id} value={map.id}>
                                {map.name || `Map #${map.id}`}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-gray-300">
                        {formatTimestamp(plaque.updated_at || plaque.updatedAt)}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            className="px-2 py-1 bg-indigo-600 text-xs rounded text-white"
                            onClick={() => setEditor({ open: true, plaqueId: plaque.id })}
                          >
                            Edit
                          </button>
                          <button
                            className="px-2 py-1 bg-red-700 text-xs rounded text-white"
                            onClick={() => handleDelete(plaque)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}


          {view === 'translations' && (
            <div className="space-y-4">
              <div className="bg-gray-950 rounded p-3 border border-gray-800 text-sm text-gray-200">
                Translate plaque text, map legend labels (titles), and map marker labels for the selected languages.
              </div>

              {translationsState.error && (
                <div className="text-sm text-red-300 bg-red-900/20 border border-red-800 rounded p-3">
                  {translationsState.error}
                </div>
              )}

              <div className="bg-gray-950 rounded p-4 border border-gray-800">
                <div className="text-lg font-semibold mb-2">Languages</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <label
                      key={lang.code}
                      className="flex items-center justify-between gap-3 bg-gray-900/60 border border-gray-800 rounded px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-gray-100 font-semibold truncate">{lang.label}</div>
                        <div className="text-xs text-gray-400 truncate">{lang.code}</div>
                      </div>
                      <input
                        type="checkbox"
                        className="accent-indigo-500"
                        checked={selectedLanguages.includes(lang.code)}
                        onChange={() => toggleLanguage(lang.code)}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-gray-950 rounded p-4 border border-gray-800">
                <div className="text-lg font-semibold mb-2">Translation Options</div>
                <label className="flex items-start gap-3 rounded border border-gray-800 bg-gray-900/60 px-3 py-3 text-sm text-gray-200">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-indigo-500"
                    checked={translationOptions.maintainTitles}
                    onChange={(e) => setTranslationOptions((prev) => ({
                      ...prev,
                      maintainTitles: e.target.checked,
                    }))}
                  />
                  <span>
                    <span className="block font-semibold text-white">Maintain titles when translating</span>
                    <span className="mt-1 block text-xs text-gray-400">
                      Enabled by default so place names and plaque titles stay in the original language while the main text is translated.
                    </span>
                  </span>
                </label>
              </div>

              <div className="bg-gray-950 rounded p-4 border border-gray-800">
                <div className="text-lg font-semibold mb-2">Prompt Override</div>
                <div className="text-xs text-gray-400 mb-2">
                  Adjust if you need to fine-tune the translation style or special wording.
                </div>
                <textarea
                  className="w-full min-h-[160px] bg-gray-900 border border-gray-700 rounded p-2 text-sm text-gray-100"
                  value={translationsPrompt}
                  onChange={(e) => setTranslationsPrompt(e.target.value)}
                />
              </div>

              <div className="bg-gray-950 rounded p-4 border border-gray-800">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold">Translate All Plaques</div>
                    <div className="text-xs text-gray-400">
                      Runs translations per plaque. This can take a while and incur cost.
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                      onClick={handleTranslateAll}
                      disabled={translationsState.running || bulkRemoveRunning || !selectedLanguages.length || rows.length === 0}
                    >
                      {translationsState.running ? 'Translating…' : 'Translate All'}
                    </button>
                    <button
                      className="px-3 py-1 rounded bg-red-700 text-white disabled:bg-red-950 disabled:text-red-200/60"
                      onClick={handleRemoveTranslationsAcrossPlaques}
                      disabled={translationsState.running || bulkRemoveRunning || !selectedLanguages.some((code) => String(code || '').toLowerCase() !== 'en') || rows.length === 0}
                    >
                      {bulkRemoveRunning ? 'Removing…' : 'Remove Selected Languages'}
                    </button>
                  </div>
                </div>

                {bulkRemoveMessage && (
                  <div className="mt-3 text-xs text-cyan-200">{bulkRemoveMessage}</div>
                )}

                {translationsState.total > 0 && (
                  <div className="mt-3 text-xs text-gray-400">
                    Completed {translationsState.completed} / {translationsState.total}
                  </div>
                )}

                {translationsState.results.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {translationsState.results.map((result) => (
                      <div
                        key={result.plaqueId}
                        className="border border-gray-800 rounded px-3 py-2 bg-gray-900/60"
                      >
                        <div className="text-sm text-gray-100 font-semibold">{result.title}</div>
                        <div className="text-xs text-gray-400">
                          {result.status === 'ok'
                            ? 'Updated'
                            : result.status === 'running'
                              ? 'Translating…'
                              : result.message || 'Translate failed.'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {editor.open && (
        <PlaqueEditorModal
          site={site}
          area={area}
          assets={assets}
          plaqueId={editor.plaqueId}
          onClose={() => setEditor({ open: false, plaqueId: null })}
          onSaved={() => {
            fetchPlaques();
            onRefreshAssets?.();
          }}
          onRefreshAssets={onRefreshAssets}
          onEditWidget={onEditWidget}
        />
      )}
    </div>
  );
}
